/**
 * Pulse Voice — bounded tone-axis option lists + illustrative examples.
 *
 * Split out of `voice-catalog.ts` (which sits at its line cap) but conceptually
 * part of the same client-side mirror. Each axis is a 5-point scale; the three
 * original values are kept as anchor stops (see `_shared/voice/types.ts`) so no
 * stored profile needs migrating. Examples are display-only — never sent to the
 * model — and graded across an axis so dragging between stops visibly changes the
 * "sounds like" preview.
 */
// 5-point scales — mirror of `_shared/voice/types.ts`. The three original values
// are kept as anchor stops so stored profiles need no migration; the rest are
// intermediate gradations. Defined here (not voice-catalog.ts) so the axis/catalog
// pair stays cycle-free; voice-catalog re-exports them for its consumers.
export type Formality = 'casual' | 'relaxed' | 'balanced' | 'professional' | 'formal';
export type Length = 'concise' | 'brief' | 'balanced' | 'thorough' | 'detailed';
export type Warmth = 'plain' | 'measured' | 'balanced' | 'warm' | 'effusive';

/** Mode shared by greeting + sign-off: auto-generate, pin a custom value, or omit. */
export type VoiceContactMode = 'none' | 'auto' | 'custom';

export interface VoiceSignOff {
  mode: VoiceContactMode;
  /** Only meaningful when `mode === 'custom'`. */
  text?: string;
}

/** Selectable option metadata for a bounded axis control. */
export interface AxisOption<T extends string> {
  value: T;
  label: string;
  /**
   * A short illustrative line showing how this setting reads. The same underlying
   * message is rendered across an axis's options so the contrast between values is
   * visible as the control changes. Display-only — never sent to the model.
   */
  example?: string;
}

export const FORMALITY_OPTIONS: readonly AxisOption<Formality>[] = [
  {
    value: 'casual',
    label: 'Casual',
    example: "Thanks for getting in touch — I'll chase this up and let you know.",
  },
  {
    value: 'relaxed',
    label: 'Relaxed',
    example: "Thanks for your email — I'll look into this and get back to you soon.",
  },
  {
    value: 'balanced',
    label: 'Balanced',
    example: "Thanks for your email. I'll follow this up and get back to you.",
  },
  {
    value: 'professional',
    label: 'Professional',
    example: "Thank you for your email. I'll follow this up and respond to you shortly.",
  },
  {
    value: 'formal',
    label: 'Formal',
    example: 'Thank you for your correspondence. I will follow this up and respond in due course.',
  },
];

export const LENGTH_OPTIONS: readonly AxisOption<Length>[] = [
  {
    value: 'concise',
    label: 'Concise',
    example: "I've referred this to the department and will update you.",
  },
  {
    value: 'brief',
    label: 'Brief',
    example: "I've referred this to the relevant department and will update you once they respond.",
  },
  {
    value: 'balanced',
    label: 'Balanced',
    example:
      "I've referred this to the relevant department and asked them to review it. I'll update you as soon as they respond.",
  },
  {
    value: 'thorough',
    label: 'Thorough',
    example:
      "I've referred this to the relevant department with your details and asked them to review it as a priority. I'll write to you as soon as I have an update.",
  },
  {
    value: 'detailed',
    label: 'Detailed',
    example:
      "I've referred this to the relevant department with your reference number and the details you provided, and asked them to review it as a priority. They've confirmed receipt, and I'll write to you as soon as I have a substantive update.",
  },
];

export const WARMTH_OPTIONS: readonly AxisOption<Warmth>[] = [
  { value: 'plain', label: 'Plain', example: "I'll follow this up and get back to you." },
  {
    value: 'measured',
    label: 'Measured',
    example: "Thanks for flagging this — I'll follow it up and get back to you.",
  },
  {
    value: 'balanced',
    label: 'Balanced',
    example: "I understand, and I'll follow this up and get back to you.",
  },
  {
    value: 'warm',
    label: 'Warm',
    example: "I completely understand your concern, and I'll follow this up and get back to you.",
  },
  {
    value: 'effusive',
    label: 'Effusive',
    example:
      "I'm so sorry you're dealing with this — I really do understand, and I'll personally follow it up and get back to you.",
  },
];

export const SIGN_OFF_MODE_OPTIONS: readonly AxisOption<VoiceSignOff['mode']>[] = [
  { value: 'auto', label: 'Automatic' },
  { value: 'custom', label: 'Custom' },
  { value: 'none', label: 'None' },
];

/** The illustrative example for the selected value of a bounded axis, if any. */
export function axisExample<T extends string>(
  options: readonly AxisOption<T>[],
  value: T,
): string | undefined {
  return options.find((option) => option.value === value)?.example;
}
